import '@mui/x-data-grid-premium/themeAugmentation'
import { Components, Theme } from '@mui/material'
import { dataGridGroupingRowVariant, dataGridRowVariantAttribute, omneaDataGridClasses } from '../../dataGridClasses'
import { menuContainerStyle, menuListStyle } from './MuiMenu'
import type { CSSObject } from '@mui/material/styles'
import type { GridValidRowModel } from '@mui/x-data-grid-premium'

// Not DataGridPremiumPropsWithoutDefaultValue: its Omit snapshots keys, so augmenting that one reaches nothing.
declare module '@mui/x-data-grid-premium' {
  // oxlint-disable-next-line @typescript-eslint/no-explicit-any
  interface DataGridPremiumProps<R extends GridValidRowModel = any> {
    variant?: 'table' | 'spreadsheet'
  }
}

type MuiDataGridOverride = NonNullable<Components<Omit<Theme, 'components'>>['MuiDataGrid']>
type DataGridSlot = keyof NonNullable<MuiDataGridOverride['styleOverrides']>

// Both variant maps satisfy this, so a slot added to one won't compile until the other defines it.
type VariantSlotStyles<Slots extends DataGridSlot> = Record<Slots, (theme: Theme) => CSSObject>

type DivergingSlotStyles = VariantSlotStyles<'root' | 'cell' | 'row' | 'row--borderBottom' | 'columnHeader'>

// The row, cell and pinned-cell rules below read these, so a row variant only ever sets them — it never
// competes with a `background-color` or `color` declaration of its own.
const rowVars = {
  background: '--omnea-DataGridRow-background',
  color: '--omnea-DataGridRow-color',
} as const

const rowBackground = (fallback: string) => `var(${rowVars.background}, ${fallback})`

const rowVariant = (variant: string) => `&[${dataGridRowVariantAttribute}='${variant}']`

const rowVariantStyles = (theme: Theme): CSSObject => ({
  [rowVariant('muted')]: {
    [rowVars.background]: theme.palette.bg.surface.subtle,
    [rowVars.color]: theme.palette.text.muted,
  },
  [rowVariant('danger')]: {
    [rowVars.background]: theme.palette.bg.danger.muted,
  },
  // The `[role="row"]` rule below forces borderWidth on all four sides, so the marker must force its own.
  [`${rowVariant('warning')}[role="row"]`]: {
    borderLeftStyle: 'solid',
    borderLeftWidth: '4px !important',
    borderLeftColor: theme.palette.border.warning.strong,
  },
  [rowVariant(dataGridGroupingRowVariant)]: {
    [rowVars.background]: theme.palette.bg.surface.subtle,
    // Grouping rows can enter the selection model, and MUI's selected background would otherwise replace
    // the header-like treatment that marks them as structure rather than data.
    '&.Mui-selected': {
      backgroundColor: rowBackground('transparent'),
    },
  },
})

const tableSlots = {
  root: (theme) => ({
    '--unstable_DataGrid-radius': theme.shape['radius-2xl'],
    borderWidth: theme.strokeWidth.default,
    '& .MuiDataGrid-cell--withRightBorder, & .MuiDataGrid-columnHeader--withRightBorder': {
      borderRightWidth: theme.strokeWidth.default,
    },
    '& .MuiDataGrid-cell--withLeftBorder, & .MuiDataGrid-columnHeader--withLeftBorder': {
      borderLeftWidth: theme.strokeWidth.default,
    },
    borderTopLeftRadius: 0,
    borderTopRightRadius: 0,

    [`&.${omneaDataGridClasses.roundTop}`]: {
      borderTopLeftRadius: theme.shape['radius-2xl'],
      borderTopRightRadius: theme.shape['radius-2xl'],
    },
    // We no longer actually round the first child, we just round the whole thing
    [`&.${omneaDataGridClasses.roundFirstChildTop}`]: {
      borderTopLeftRadius: theme.shape['radius-2xl'],
      borderTopRightRadius: theme.shape['radius-2xl'],
    },
  }),
  cell: (theme) => ({
    padding: theme.spacing(3),
    borderTopWidth: theme.strokeWidth.default,
  }),
  row: (theme) => ({
    '&:hover': {
      // MUI's own `.MuiDataGrid-row:hover` rule out-specifies the row background, so restate it. The hover
      // token is semi-transparent, so layering it as an image composites it over a row variant's
      // background colour instead of replacing it.
      backgroundColor: rowBackground('transparent'),
      backgroundImage: `linear-gradient(${theme.palette.bg.interactive.hover}, ${theme.palette.bg.interactive.hover})`,
    },
  }),
  'row--borderBottom': (theme) => ({
    '& .MuiDataGrid-columnHeader, & .MuiDataGrid-filler, & .MuiDataGrid-scrollbarFiller': {
      borderBottomWidth: theme.strokeWidth.default,
    },
  }),
  columnHeader: () => ({}),
} satisfies DivergingSlotStyles

const spreadsheetSlots = {
  root: (theme) => ({
    // Every radius MUI applies derives from this, including the header cells' own inner corners.
    '--unstable_DataGrid-radius': '0px',

    borderWidth: theme.strokeWidth.strong,
    '& .MuiDataGrid-cell--withRightBorder, & .MuiDataGrid-columnHeader--withRightBorder': {
      borderRightWidth: theme.strokeWidth.strong,
    },
  }),
  cell: (theme) => ({
    // The cell components own the inset and the background, so the cell only supplies the box.
    padding: 0,
    borderTopWidth: theme.strokeWidth.strong,

    // Suppresses the grid's own cell focus ring, which always-on inputs make read as noise.
    '&:focus, &:focus-within': {
      outline: 'none',
    },
  }),
  row: () => ({
    // Suppresses the grid's own row hover, which a spreadsheet shouldn't have, while leaving a row
    // variant's background in place.
    '&:hover': {
      backgroundColor: rowBackground('transparent'),
    },
  }),
  'row--borderBottom': (theme) => ({
    '& .MuiDataGrid-columnHeader, & .MuiDataGrid-filler, & .MuiDataGrid-scrollbarFiller': {
      borderBottomWidth: theme.strokeWidth.strong,
    },
  }),
  columnHeader: (theme) => ({
    '&.MuiDataGrid-columnHeader--filledGroup': {
      borderBottomWidth: theme.strokeWidth.strong,
      borderBottomStyle: 'solid',
    },
    '&.MuiDataGrid-columnHeader--filledGroup .MuiDataGrid-columnHeaderTitleContainer': {
      borderBottom: 'none',
    },
    '&.MuiDataGrid-columnHeader--filledGroup .MuiDataGrid-columnHeaderTitleContainerContent': {
      width: 'auto',
    },
    '&.MuiDataGrid-columnHeader--alignCenter .MuiDataGrid-columnHeaderTitleContainerContent': {
      width: 'auto',
    },
    // MUI sets the cell height inline, so min-height (not height) wins; --DataGrid-headerHeight is one row,
    // --DataGrid-headersTotalHeight is all of them.
    [`&.${omneaDataGridClasses.mergedGroupHeader}`]: {
      minHeight: 'var(--DataGrid-headersTotalHeight)',
      marginTop: 'calc(var(--DataGrid-headerHeight) - var(--DataGrid-headersTotalHeight))',
    },
  }),
} satisfies DivergingSlotStyles

// MUI runs every slot override for every component it names MuiDataGrid, and some (the overlay wrappers) pass no ownerState.
const variantSlots = (ownerState?: { variant?: 'table' | 'spreadsheet' }): DivergingSlotStyles =>
  ownerState?.variant === 'spreadsheet' ? spreadsheetSlots : tableSlots

export const MuiDataGrid: Components<Omit<Theme, 'components'>>['MuiDataGrid'] = {
  defaultProps: {
    variant: 'table',
    slotProps: {
      // e.g. icon buttons in the headers for sort/filter
      baseIconButton: {
        variant: 'ghost',
      },
    },
  },
  styleOverrides: {
    root: ({ theme, ownerState }) => ({
      '--DataGrid-overlayHeight': '300px',
      '--DataGrid-rowBorderColor': theme.palette.border.strong,
      '--DataGrid-pinnedBackground': theme.palette.bg.surface.default,
      '--DataGrid-containerBackground': theme.palette.bg.surface.default,
      borderColor: theme.palette.border.strong,
      color: theme.palette.text.default,

      '& .MuiDataGrid-withBorderColor': {
        borderColor: theme.palette.border.strong,
      },

      // A connected mouse, every Windows user, and any Mac set to always show scrollbars force a persistent scrollbar,
      // which MUI subtracts from this height until the skeleton collapses.
      '& .MuiDataGrid-main--hasSkeletonLoadingOverlay': {
        minHeight: 'calc(var(--DataGrid-headersTotalHeight) + var(--DataGrid-overlayHeight))',
      },

      [`&.${omneaDataGridClasses.allowPageScroll}`]: {
        '& .MuiDataGrid-virtualScroller': {
          overflowY: 'visible !important',
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': {
            display: 'none',
          },
        },
      },

      [`&.${omneaDataGridClasses.clickableRows}`]: {
        '& .MuiDataGrid-row': {
          cursor: 'pointer',
        },
      },
      // Direct filler overrides don't work?
      '& .MuiDataGrid-filler > div': {
        borderTopWidth: theme.strokeWidth.default,
      },

      ...variantSlots(ownerState).root(theme),
    }),
    cell: ({ theme, ownerState }) => ({
      ...theme.typography['body-regular'],
      color: `var(${rowVars.color}, ${theme.palette.text.default})`,
      alignItems: 'center',
      display: 'flex',
      ...variantSlots(ownerState).cell(theme),
      [`&.${omneaDataGridClasses.highlightedCell}`]: {
        backgroundColor: theme.palette.bg.utility.gold.muted,
      },
      [`&.${omneaDataGridClasses.successCell}`]: {
        backgroundColor: theme.palette.bg.success.muted,
        color: theme.palette.fg.success.onSuccessMuted,
      },
      [`&.${omneaDataGridClasses.dangerCell}`]: {
        backgroundColor: theme.palette.bg.danger.muted,
        color: theme.palette.fg.danger.onDangerMuted,
      },
      // A full-row group header keeps its label in view while scrolling; the cell must stop clipping for sticky to work.
      '&:not([aria-colspan="1"]):has(> .MuiDataGrid-groupingCriteriaCell)': {
        overflow: 'visible',
        '& > .MuiDataGrid-groupingCriteriaCell': {
          position: 'sticky',
          left: 0,
          width: 'max-content',
          flex: 'none',
        },
      },
    }),
    // A spacer with no content, so MUI declares `padding: 0` for it — but the `cell` override above
    // out-ranks that, and the padding then carries every row past the last column.
    cellEmpty: {
      padding: 0,
    },
    columnHeader: ({ theme, ownerState }) => ({
      padding: theme.spacing(3),
      ...theme.typography['body-medium'],
      color: theme.palette.text.muted,
      ...variantSlots(ownerState).columnHeader(theme),
    }),
    'row--borderBottom': ({ theme, ownerState }) => variantSlots(ownerState)['row--borderBottom'](theme),
    'container--top': ({ theme }) => ({
      '& [role="row"]': {
        backgroundColor: theme.palette.bg.surface.subtle,
      },
    }),
    'container--bottom': ({ theme }) => ({
      '& [role="row"]': {
        backgroundColor: theme.palette.bg.surface.subtle,
      },
    }),
    row: ({ theme, ownerState }) => ({
      backgroundColor: rowBackground('transparent'),
      // DataGridBase uses a nested selector and !important, so we have to do a lot to override it here
      // We should remove most of that styling and move it here
      '&[role="row"]': {
        borderWidth: `${theme.strokeWidth.default} !important`,
        borderColor: theme.palette.border.strong,
      },
      ...rowVariantStyles(theme),
      ...variantSlots(ownerState).row(theme),
    }),
    // MUI derives the separator colour from `palette.divider` by forcing its alpha to 1 and then
    // lightening it 88%, which only lands on a usable grey while divider is a low-alpha near-black.
    // Ours is an opaque token, so the derived value comes out near-white — set it explicitly.
    columnSeparator: ({ theme }) => ({
      opacity: 0,
      color: theme.palette.border.subtle,
    }),
    virtualScroller: ({ theme }) => ({
      backgroundColor: theme.palette.bg.surface.default,
    }),
    menu: ({ theme }) => ({
      '& .MuiPaper-root': menuContainerStyle(theme),
      '& .MuiDataGrid-menuList': menuListStyle(theme),
    }),
    panel: ({ theme }) => ({
      boxShadow: theme.customShadows.sm,
    }),
    footerContainer: ({ theme }) => ({
      backgroundColor: theme.palette.bg.surface.default,
      borderTopWidth: theme.strokeWidth.default,
    }),
    pinnedColumns: {
      zIndex: 1,
    },
    'columnHeader--pinnedLeft': ({ theme }) => ({
      zIndex: 1,
      backgroundColor: theme.palette.bg.surface.default,
    }),
    'columnHeader--pinnedRight': ({ theme }) => ({
      zIndex: 1,
      backgroundColor: theme.palette.bg.surface.default,
    }),
    // Pinned cells paint their own opaque background over scrolled content, so they have to honour a row
    // variant's colour or the variant would stop at the pinned columns.
    'cell--pinnedLeft': ({ theme }) => ({
      backgroundColor: rowBackground(theme.palette.bg.surface.default),
    }),
    'cell--pinnedRight': ({ theme }) => ({
      backgroundColor: rowBackground(theme.palette.bg.surface.default),
    }),
    'filler--pinnedLeft': ({ theme }) => ({
      backgroundColor: theme.palette.bg.surface.default,
    }),
    'filler--pinnedRight': ({ theme }) => ({
      backgroundColor: theme.palette.bg.surface.default,
    }),
    paper: ({ theme }) => ({
      borderWidth: theme.strokeWidth.default,
      borderStyle: 'solid',
      borderColor: theme.palette.border.subtle,
      borderRadius: theme.shape['radius-sm'],
      color: theme.palette.text.default,
      backgroundColor: theme.palette.bg.surface.elevated,
    }),
    columnHeaderTitleContainer: {
      // Sort icon in header shouldn't be clipped
      overflow: 'unset',
    },
    columnHeaderTitleContainerContent: {
      // Move sort icon to the right
      width: '100%',
    },
    aggregationColumnHeaderLabel: ({ theme }) => ({
      ...theme.typography['body-regular'],
    }),
    filterIcon: ({ theme }) => ({
      color: theme.palette.fg.muted,
    }),
    filterForm: ({ theme }) => ({
      gap: theme.spacing(1),
    }),
    panelFooter: ({ theme }) => ({
      marginTop: theme.spacing(4),
    }),
    columnsManagement: ({ theme }) => ({
      padding: theme.spacing(2),
      '& .MuiDataGrid-columnsManagementRow': {
        // Default is a negative margin
        marginLeft: 0,
        '& .MuiCheckbox-root': {
          // Aligned with default padding on Checkbox - DataGrid overrides it
          padding: theme.spacing(2),
        },
      },
    }),
    columnsManagementFooter: ({ theme }) => ({
      borderTopWidth: theme.strokeWidth.default,
      borderColor: theme.palette.border.subtle,
      padding: theme.spacing(2),
      '& .MuiFormControlLabel-root': {
        marginLeft: 0,

        '& .MuiCheckbox-root': {
          // Aligned with default padding on Checkbox - DataGrid overrides it
          padding: theme.spacing(2),
        },
      },
    }),
  },
}
